import {
  IDecryptedAuthInfo,
  IEncryptedAuthInfo,
} from "../../interfaces/AuthInfo";
import {
  CURRENT_KDF_ITERATIONS,
  LEGACY_KDF_ITERATIONS,
  decrypt,
  encrypt,
} from "./encryption";

export enum DecryptAccountStatusEnum {
  INVALID_PASSWORD = "invalid_password",
  MISSING_IV_SALT = "missing_iv_salt",
  OK = "ok",
}

/**
 * Decrypts account
 * If ok, returns status OK and the decryptedInfo. Else the decryptedInfo is undefined
 * @param info
 * @param password
 * @returns
 */
export const decryptEncryptedAccountInfo = (
  info: IEncryptedAuthInfo,
  password: string
) => {
  if (!info.iv || !info.salt) {
    return {
      status: DecryptAccountStatusEnum.MISSING_IV_SALT,
      decryptedInfo: undefined,
      needsRehash: false,
    };
  }

  try {
    // A record with no `kdfIterations` predates the field and was written at
    // the legacy count. Reading it as anything else would make an existing
    // credential permanently undecryptable.
    //
    // ABSENT and INVALID are different, and must not share a branch. This was
    // `Number(info.kdfIterations) || LEGACY`, where `||` swallowed 0, NaN and
    // any unparseable string into the legacy default — silently deriving with
    // the wrong cost and reporting the result as a bad password. A value that is
    // present but not a positive integer means the record is corrupt, so say so
    // rather than guessing a count for it.
    const rawIterations = info.kdfIterations;
    let iterations: number;
    if (rawIterations === undefined || rawIterations === null || rawIterations === "") {
      iterations = LEGACY_KDF_ITERATIONS;
    } else {
      iterations = Number(rawIterations);
      if (!Number.isInteger(iterations) || iterations <= 0) {
        throw new Error(
          `Corrupt credential: kdfIterations is ${JSON.stringify(rawIterations)}`
        );
      }
    }
    const decryptedString = decrypt(
      info.encryptedBundle,
      password,
      info.iv,
      info.salt,
      iterations
    );
    const parsed: IDecryptedAuthInfo = {
      ...(JSON.parse(decryptedString) as IDecryptedAuthInfo),
      // set encrypted to false
      encrypted: false,
    };

    // Check valid account
    if (parsed.account !== info.account) {
      return {
        status: DecryptAccountStatusEnum.INVALID_PASSWORD,
        decryptedInfo: undefined,
        needsRehash: false,
      };
    }

    return {
      status: DecryptAccountStatusEnum.OK,
      decryptedInfo: parsed,
      /**
       * True when this record was derived at an older iteration count.
       *
       * The password is only in memory at this moment, so this is the one point
       * where the record can be re-wrapped at the current strength. Callers that
       * hold the password may re-encrypt and persist; ignoring it is safe and
       * simply leaves the record at its old cost.
       */
      needsRehash: iterations !== CURRENT_KDF_ITERATIONS,
    };
  } catch (error) {
    // Error return invalid password
    return {
      status: DecryptAccountStatusEnum.INVALID_PASSWORD,
      decryptedInfo: undefined,
      needsRehash: false,
    };
  }
};

export const encryptAccountInfo = (
  info: IDecryptedAuthInfo,
  password: string,
  /**
   * Overridable only so tests can derive at the cheap legacy cost — the real
   * count takes ~1s per call by design, and a suite that pays that dozens of
   * times stops being run. Production callers omit it.
   */
  iterations?: number
) => {
  const encryptedString = encrypt(JSON.stringify(info), password, iterations);
  return encryptedString;
};
